import i18next, { ExistsFunction, TFunctionKeys, TOptions } from 'i18next';
import { ReactNode, useState } from 'react';
import useOnMount from '../hooks/useOnMount';
import { supportedLanguages } from './languages';
import { ErrorData } from '../api/types';

type _TFunction = (key: TFunctionKeys | TFunctionKeys[], options?: TOptions) => string;

const t: _TFunction = i18next.t.bind(i18next);
const exists: ExistsFunction = i18next.exists.bind(i18next);

const mapJpaSystemExceptionErrorCode = (errorObj: ErrorData) => {
  const matchAllQuotes = [errorObj.constraint, errorObj.message?.split('"'), errorObj.category].filter(Boolean);
  const key = matchAllQuotes.map(item => `global.serverException.constraints.${item}`).find(item => exists(item));
  return key ? t(key) : null;
};


export const formatBackendErrorMessage = (errorJson: ErrorData, fallbackMsg: string = 'Error') => {
  let errorMessage: string;
  const key = `global.serverException.${errorJson.errorCode}`;
  if (errorJson.exception === 'UnauthorizedException') {
    errorMessage = errorJson.message || '';
  } else if (errorJson.exception === 'JpaSystemException' && !errorJson.errorCode && (errorMessage = mapJpaSystemExceptionErrorCode(errorJson))) {
    // if no errorMessage - skip to next condition
  } else if (exists(key)) {
    errorMessage = t(key, errorJson.arguments);
  } else if (errorJson.errorCode && typeof errorJson.errorCode !== 'string' && 'message' in errorJson.errorCode) {
    errorMessage = errorJson.errorCode.message;
  } else {
    errorMessage = fallbackMsg;
  }
  return errorMessage;
};

// for backbone
try {
  window.t = (key, ...args) => {
    if (typeof args[0] === 'object') {
      return t(key, ...args);
    } else {
      return t(key, args);
    }
  };
  window.translateExists = exists;
// eslint-disable-next-line no-empty
} catch (e) {}

export const changeLanguage = async (language: string, isPublic = false) => {
  if (!language) {
    return console.error('No language has been provided for change');
  }
  if (i18next.hasResourceBundle(language, 'translation')) {
    return i18next.changeLanguage(language);
  } else {
    const supportedLanguage = supportedLanguages[language as keyof typeof supportedLanguages];
    if (!supportedLanguage) {
      return console.error(`Language "${language}" is not supported`);
    }
    const resource = await import(`./locales/${isPublic ? 'public' : 'client'}_${supportedLanguage}.json`);
    i18next.addResourceBundle(language,  'translation', resource);
    return i18next.changeLanguage(language);
  }
};

/**
 * Hook to trigger re-render on language change
 * @returns {string} - language
 */
export const useLanguage = (): string => {

  const [state, setState] = useState(i18next.language);

  useOnMount(() => {
    const cb = function (language: string) {
      setState(language);
    };

    i18next.on('languageChanged', cb);

    return () => {
      i18next.off('languageChanged', cb);
    };
  });

  return state;
};

type InterpolateToJsxType = {
  textKey: string;
  params: Record<string, ReactNode>;
  context?: string;
}

export const InterpolateToJSX = ({ textKey, params, context }: InterpolateToJsxType) => {
  const preparedArray = t(textKey, { interpolation: { prefix: '$${', suffix: '}$$' }, context }) // disabling interpolation
    .replace(/}(.?)/gm, '}%%$1')
    .replace(/(.?)\{/gm, '$1%%{')
    .split('%%');

  return <>{preparedArray.map(s => s.startsWith('{') ? params?.[s.substring(1, s.length - 1)] || s : s)}</>;
};

/**
 *
 * @returns {Object.<string, string>} {countryCode: name} where name is i18n'd
 */
export const useCountries = (): Record<string, string> => {
  return window.tbglobal.countries; // from backbone global for now
};

export const importLibLocale = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  importGlobList: Record<string, () => Promise<any>>,
  language: string,
  findPredicate = (key: string) => key.endsWith(language + '.js')
) => {
  const importInstanceKey = Object.keys(importGlobList).find(findPredicate);
  if (importInstanceKey) {
    return importGlobList[importInstanceKey]();
  } else if (CONFIG.debug) {
    const firstKey = Object.keys(importGlobList)[0];
    console.log(language, 'is missing from ', firstKey.substring(0, firstKey.lastIndexOf('/')));
  }
};

export { i18next, t, exists };
